// Landing + conflict feedback for module drag, ported from PRO100 Online's
// scene interior handle (`.scene-interior-control.is-invalid` → `hz-boundary-recoil`)
// and its placement-offer entrance (`placement-offer-in`).
//
// PRO100 answers two questions the plain colour-swap of the move-ghost
// (`moduleMovePreview.ts`) does not: "did it actually land?" and "why did it
// just refuse to move?". It answers both with *motion*, not just colour — a
// settle ring where a module lands, and a quick recoil shake when a drag is
// refused. Both are pure functions of elapsed time so the render loop can
// stay a thin, disposable consumer: it reads a number for the current frame
// and never owns a timer, an easing curve, or a decision about *whether* to
// animate.
//
// Neither function touches the module's own transform. The recoil offsets
// the (already purely cosmetic) drag ghost; the landing pulse is a new,
// separate ring primitive at the footprint. Scaling the module's own anchor
// would feed a wrong number into `collectConflictMarkers`' `scale !== 1`
// check for the ~200ms the bounce is in flight — a fixed bug looking for a
// reason to happen again.

import { clamp01, easeInOutCubic } from '../sceneCanvasPolicy';
import type { MovePreviewState } from '../moduleMovePreview';

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Standard cubic ease-out: fast start, soft settle. */
export function easeOutCubic(t: number): number {
  const c = clamp01(t);
  const inv = 1 - c;
  return 1 - inv * inv * inv;
}

/**
 * Robert Penner's "ease out back": overshoots past 1 before settling. Used so
 * a landing ring visibly *expands past* its resting size instead of just
 * fading in — the difference between "arrived" and "was always there".
 */
export function easeOutBack(t: number, overshoot = 1.70158): number {
  const c = clamp01(t);
  const x = c - 1;
  return 1 + (overshoot + 1) * x * x * x + overshoot * x * x;
}

export const LANDING_PULSE_DURATION_MS = 260;
const LANDING_PULSE_START_SCALE = 1;
const LANDING_PULSE_END_SCALE = 1.22;
const LANDING_PULSE_START_OPACITY = 0.85;

export type LandingPulseFrame = Readonly<{ scale: number; opacity: number; done: boolean }>;

/**
 * A ring at the module's landing footprint: starts at the footprint's own
 * size and full-ish opacity, expands outward with an ease-out-back overshoot,
 * and fades to nothing. `elapsedMs` is time since the drag that placed the
 * module ended without a refusal — the render loop owns the clock, this only
 * answers "what does frame N look like".
 */
export function landingPulseFrame(
  elapsedMs: number,
  durationMs: number = LANDING_PULSE_DURATION_MS,
): LandingPulseFrame {
  if (durationMs <= 0) return { scale: LANDING_PULSE_END_SCALE, opacity: 0, done: true };
  const t = elapsedMs / durationMs;
  if (t >= 1) return { scale: LANDING_PULSE_END_SCALE, opacity: 0, done: true };
  if (t <= 0) return { scale: LANDING_PULSE_START_SCALE, opacity: LANDING_PULSE_START_OPACITY, done: false };
  return {
    scale: lerp(LANDING_PULSE_START_SCALE, LANDING_PULSE_END_SCALE, easeOutBack(t)),
    opacity: lerp(LANDING_PULSE_START_OPACITY, 0, easeOutCubic(t)),
    done: false,
  };
}

export const CONFLICT_SHAKE_DURATION_MS = 180;
export const CONFLICT_SHAKE_AMPLITUDE_MM = 6;

/**
 * PRO100's `hz-boundary-recoil` keyframes, verbatim: 0 → -amplitude at 35% →
 * +amplitude at 70% → 0 at 100%, linear between. A shake, not a decay curve —
 * the point is one sharp "no" in the direction the drag was pushing, not a
 * spring that keeps ringing.
 */
export function conflictShakeOffsetMm(
  elapsedMs: number,
  durationMs: number = CONFLICT_SHAKE_DURATION_MS,
  amplitudeMm: number = CONFLICT_SHAKE_AMPLITUDE_MM,
): number {
  if (durationMs <= 0 || amplitudeMm === 0) return 0;
  const t = elapsedMs / durationMs;
  if (t <= 0 || t >= 1) return 0;
  if (t <= 0.35) return lerp(0, -amplitudeMm, t / 0.35);
  if (t <= 0.7) return lerp(-amplitudeMm, amplitudeMm, (t - 0.35) / 0.35);
  return lerp(amplitudeMm, 0, (t - 0.7) / 0.3);
}

/**
 * The recoil is a one-shot "just refused" pulse, not a held state: it should
 * fire the instant a drag's move-preview state *becomes* invalid/locked, and
 * stay silent for every following frame that is still invalid/locked (the
 * ghost's red tint already carries that ongoing fact). Re-arms on the next
 * genuine transition, including invalid→locked — a different refusal reason
 * deserves its own "no".
 */
export function conflictJustStarted(
  previous: MovePreviewState | null,
  next: MovePreviewState | null,
): boolean {
  if (next !== 'invalid' && next !== 'locked') return false;
  return previous !== next;
}

// --- Idle handle affordances: hz-scene-handle-float, hz-scene-arrow(-y) ---
//
// PRO100's interior-push control (`.scene-interior-control>button`) floats
// gently and its two direction arrows nudge apart, for as long as the
// control is on screen — an idle "this is grabbable, drag it along this
// axis" cue, not a state transition. Our equivalent handle is the module's
// plan-anchor marker (`anchorMarkerRef` in useModuleEditing.ts): a small
// sphere shown at the active anchor point while a module/detail is being
// edited. "Infinite" here means the same thing it means in PRO100's CSS —
// runs for as long as the handle itself is visible, driven by an absolute
// clock (`performance.now()`) so there is no per-frame restart to get wrong.

export const HANDLE_FLOAT_PERIOD_MS = 1700; // hz-scene-handle-float: 1.7s ease-in-out infinite
export const HANDLE_FLOAT_AMPLITUDE_MM = 3; // 50% keyframe: translateY(-3px)

function triangleWaveMm(elapsedMs: number, periodMs: number, amplitudeMm: number): number {
  if (periodMs <= 0) return 0;
  const phase = (((elapsedMs % periodMs) + periodMs) % periodMs) / periodMs;
  return phase <= 0.5
    ? lerp(0, -amplitudeMm, easeInOutCubic(phase / 0.5))
    : lerp(-amplitudeMm, 0, easeInOutCubic((phase - 0.5) / 0.5));
}

/**
 * PRO100's `hz-scene-handle-float` keyframes, verbatim: 0%/100% at rest, 50%
 * at `translateY(-3px)`, 1.7s. A symmetric down-and-back bob computed from an
 * absolute clock, so a handle that has been visible for any length of time
 * reads the correct phase on the very first frame it is drawn — no local
 * "time since I started" state to desync from a sibling handle.
 */
export function handleFloatOffsetMm(
  elapsedMs: number,
  periodMs: number = HANDLE_FLOAT_PERIOD_MS,
  amplitudeMm: number = HANDLE_FLOAT_AMPLITUDE_MM,
): number {
  return triangleWaveMm(elapsedMs, periodMs, amplitudeMm);
}

export const DIRECTION_ARROW_PERIOD_MS = 1200; // hz-scene-arrow / hz-scene-arrow-y: 1.2s ease-in-out infinite
export const DIRECTION_ARROW_AMPLITUDE_MM = 3; // 50% keyframe: ±3px

/**
 * PRO100's `hz-scene-arrow` / `hz-scene-arrow-y`: the same down-and-back
 * shape as the handle float, at 1.2s instead of 1.7s. PRO100 plays the
 * second arrow (`i:last-child`) with `animation-direction:reverse` — for a
 * waveform that starts and ends at the same rest value, reversing the
 * timeline lands on an identical curve (the shape is symmetric about its own
 * midpoint), so one function serves both arrows; the caller picks which
 * spatial axis the offset drives (X for the default pair, Y for
 * `[data-axis="y"]`).
 */
export function directionArrowOffsetMm(
  elapsedMs: number,
  periodMs: number = DIRECTION_ARROW_PERIOD_MS,
  amplitudeMm: number = DIRECTION_ARROW_AMPLITUDE_MM,
): number {
  return triangleWaveMm(elapsedMs, periodMs, amplitudeMm);
}

// --- Part pulse: hz-part-pulse ---

export const PART_PULSE_PERIOD_MS = 1800; // hz-part-pulse: 1.8s ease-out infinite
export const PART_PULSE_MAX_SPREAD_MM = 8; // box-shadow spread 0 -> 8px
export const PART_PULSE_START_ALPHA = 0.38; // rgb(46 139 89 / 38%)
export const PART_PULSE_HOLD_FRACTION = 0.7; // `70%,100%{...}` — reaches the end value at 70%, holds till 100%

export type PartPulseFrame = Readonly<{ spreadMm: number; alpha: number }>;

/**
 * PRO100's `hz-part-pulse` (`.part-atelier-pulse`, the "this part is live"
 * indicator dot): a ring that grows from 0 to 8px of spread while its colour
 * fades from 38% to 0% alpha, reaching the end state at 70% of the 1.8s
 * cycle and holding there for the rest (`70%,100%{...}` is two keyframes at
 * the SAME value, i.e. a pause before the loop restarts). Loops for as long
 * as a part stays selected — a heartbeat, not a one-shot.
 */
export function partPulseFrame(
  elapsedMs: number,
  periodMs: number = PART_PULSE_PERIOD_MS,
): PartPulseFrame {
  if (periodMs <= 0) return { spreadMm: PART_PULSE_MAX_SPREAD_MM, alpha: 0 };
  const phase = (((elapsedMs % periodMs) + periodMs) % periodMs) / periodMs;
  if (phase >= PART_PULSE_HOLD_FRACTION) return { spreadMm: PART_PULSE_MAX_SPREAD_MM, alpha: 0 };
  const t = easeOutCubic(phase / PART_PULSE_HOLD_FRACTION);
  return {
    spreadMm: lerp(0, PART_PULSE_MAX_SPREAD_MM, t),
    alpha: lerp(PART_PULSE_START_ALPHA, 0, t),
  };
}

// --- Nudge at the limit: handle-nudge, hz-soft-nudge ---
//
// PRO100 loops both forever as ambient "look at me" cues with no state
// behind them (`.module-resize-handle::before`'s arrow, a badge, a CTA
// button). Our magnetic live-snap (`livesnapToNeighbour` in
// `useModuleEditing.ts`) actually has a state edge worth marking: the exact
// frame a drag crosses `LIVE_SNAP_THRESHOLD_MM` and the solver pulls the
// module flush against its neighbour. A real "catch" earns one nudge, not a
// permanent fidget — so both are ported as ONE PLAYTHROUGH of the source
// loop, fired at that edge (see `snapNudgeJustEngaged`), the same
// "transition, not every frame" rule already applied to the conflict recoil.

export const HANDLE_NUDGE_DURATION_MS = 1500; // handle-nudge: 1.5s ease-in-out infinite
export const HANDLE_NUDGE_MIN_MM = -1; // 0%,100%: translateX(-1px), opacity .85
export const HANDLE_NUDGE_MAX_MM = 2; // 50%: translateX(2px), opacity 1
export const HANDLE_NUDGE_MIN_OPACITY = 0.85;
export const HANDLE_NUDGE_MAX_OPACITY = 1;

export type HandleNudgeFrame = Readonly<{ offsetMm: number; opacity: number }>;

/**
 * PRO100's `handle-nudge` (`.module-resize-handle::before`), verbatim: an
 * asymmetric idle wiggle between -1px/.85 and +2px/1, 1.5s ease-in-out.
 * Ported as pure numbers only — this project has no continuously-visible
 * resize-handle icon to host an ambient idle wiggle without inventing new
 * chrome, which the brief explicitly excludes. Kept here, tested, and ready
 * for the day such a handle exists; the wired "подталкивание при упоре" is
 * `softNudgeOffsetMm` below.
 */
export function handleNudgeFrame(
  elapsedMs: number,
  durationMs: number = HANDLE_NUDGE_DURATION_MS,
): HandleNudgeFrame {
  const t = clamp01(elapsedMs / durationMs);
  const local = t <= 0.5 ? easeInOutCubic(t / 0.5) : easeInOutCubic((t - 0.5) / 0.5);
  return t <= 0.5
    ? {
        offsetMm: lerp(HANDLE_NUDGE_MIN_MM, HANDLE_NUDGE_MAX_MM, local),
        opacity: lerp(HANDLE_NUDGE_MIN_OPACITY, HANDLE_NUDGE_MAX_OPACITY, local),
      }
    : {
        offsetMm: lerp(HANDLE_NUDGE_MAX_MM, HANDLE_NUDGE_MIN_MM, local),
        opacity: lerp(HANDLE_NUDGE_MAX_OPACITY, HANDLE_NUDGE_MIN_OPACITY, local),
      };
}

export const SOFT_NUDGE_DURATION_MS = 1800; // hz-soft-nudge on `.part-atelier-toggle em` (badge): 1.8s
export const SOFT_NUDGE_BUTTON_DURATION_MS = 2200; // hz-soft-nudge on `.nad-actions .nad-primary`: 2.2s
export const SOFT_NUDGE_AMPLITUDE_MM = 3; // 50% keyframe: translateX(-3px)

/**
 * PRO100's `hz-soft-nudge`, verbatim: 0 at both ends, -3px at the midpoint.
 * Played as a single cycle, triggered by `snapNudgeJustEngaged`, on the
 * module's move-footprint (`footprintFillRef` / `footprintLoopRef` in
 * `useModuleEditing.ts`) — the one overlay primitive guaranteed on screen
 * for the WHOLE translate drag, valid or not, unlike the ghost box which
 * only appears once a drag disagrees with the cursor.
 */
export function softNudgeOffsetMm(
  elapsedMs: number,
  durationMs: number = SOFT_NUDGE_DURATION_MS,
  amplitudeMm: number = SOFT_NUDGE_AMPLITUDE_MM,
): number {
  if (durationMs <= 0 || amplitudeMm === 0) return 0;
  const t = elapsedMs / durationMs;
  if (t <= 0 || t >= 1) return 0;
  if (t <= 0.5) return lerp(0, -amplitudeMm, easeInOutCubic(t / 0.5));
  return lerp(-amplitudeMm, 0, easeInOutCubic((t - 0.5) / 0.5));
}

/**
 * The snap nudge is a one-shot "just caught" pulse: it should fire the
 * instant `livesnapToNeighbour`'s per-frame correction goes from zero to
 * non-zero, and stay silent on every following frame the module is still
 * held at that same flush edge (where the correction is naturally zero
 * again, because the module is already flush). Mirrors `conflictJustStarted`.
 */
export function snapNudgeJustEngaged(previousDeltaMm: number, nextDeltaMm: number): boolean {
  return nextDeltaMm !== 0 && previousDeltaMm === 0;
}
